import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { queryKeys } from "@/config/queryClient"
import { getOrgSettings, getOrgAvailableModules, getOrgBranding } from "@/service/_apiService"

/**
 * Función para aplicar estilos de branding inmediatamente
 */
const applyBrandingStyles = (bannerStyles: any) => {
  try {
    // Crear o actualizar CSS variables para estilos de branding
    const root = document.documentElement
    
    if (bannerStyles.main) {
      root.style.setProperty('--brand-primary', bannerStyles.main)
    }
    if (bannerStyles.secondary) {
      root.style.setProperty('--brand-secondary', bannerStyles.secondary)
    }
    if (bannerStyles.headerBarColor) {
      root.style.setProperty('--brand-header', bannerStyles.headerBarColor)
    }
    if (bannerStyles.SentRecogCard) {
      root.style.setProperty('--brand-sent-recog', bannerStyles.SentRecogCard)
    }
    if (bannerStyles.ReceivedRecogCard) {
      root.style.setProperty('--brand-received-recog', bannerStyles.ReceivedRecogCard)
    }
    if (bannerStyles.BadgesCard) {
      root.style.setProperty('--brand-badges', bannerStyles.BadgesCard)
    }
    if (bannerStyles.CardsFontColor) {
      root.style.setProperty('--brand-cards-font', bannerStyles.CardsFontColor)
    }
    
    console.log("🎨 Branding styles applied to CSS variables")
  } catch (error) {
    console.warn("⚠️ Error applying branding styles:", error)
  }
}

/**
 * Hook para prefetch de datos críticos
 */
export const usePrefetchCriticalData = () => {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Prefetch datos críticos en paralelo
    const prefetchCriticalData = async () => {
      try {
        await Promise.allSettled([
          // Prefetch configuración de la organización
          queryClient.prefetchQuery({
            queryKey: queryKeys.orgSettings,
            queryFn: getOrgSettings,
            staleTime: 5 * 60 * 1000,
          }),

          // Prefetch módulos disponibles
          // Branding NO se prefetchea aquí: useLoadBrandingImmediately ya lo carga
          // y lo guarda con setQueryData evitando una llamada duplicada
          queryClient.prefetchQuery({
            queryKey: queryKeys.orgModules,
            queryFn: getOrgAvailableModules,
            staleTime: 10 * 60 * 1000,
          }),
        ])

        console.log("✅ Critical data prefetched successfully")
      } catch (error) {
        console.warn("⚠️ Error prefetching critical data:", error)
      }
    }

    // Ejecutar prefetch inmediatamente para branding crítico
    const timeoutId = setTimeout(prefetchCriticalData, 100)

    return () => clearTimeout(timeoutId)
  }, [queryClient])
}

/**
 * Hook para prefetch de datos de usuario
 * Comentado temporalmente hasta que se definan las funciones correspondientes
 */
// export const usePrefetchUserData = (userId?: string | number) => {
//   const queryClient = useQueryClient();

//   useEffect(() => {
//     if (!userId) return;

//     const prefetchUserData = async () => {
//       try {
//         await Promise.allSettled([
//           // Prefetch perfil de usuario
//           queryClient.prefetchQuery({
//             queryKey: [...queryKeys.user, userId],
//             staleTime: 5 * 60 * 1000,
//           }),

//           // Prefetch fotos de perfil
//           queryClient.prefetchQuery({
//             queryKey: [...queryKeys.userProfilePhotos, userId],
//             staleTime: 10 * 60 * 1000,
//           }),
//         ]);

//         console.log('✅ User data prefetched successfully');
//       } catch (error) {
//         console.warn('⚠️ Error prefetching user data:', error);
//       }
//     };

//     const timeoutId = setTimeout(prefetchUserData, 2000);

//     return () => clearTimeout(timeoutId);
//   }, [queryClient, userId]);
// };

/**
 * Hook para prefetch de datos del catálogo
 * Comentado temporalmente hasta que se definan las funciones correspondientes
 */
// export const usePrefetchCatalogData = () => {
//   const queryClient = useQueryClient();

//   useEffect(() => {
//     const prefetchCatalogData = async () => {
//       try {
//         await Promise.allSettled([
//           // Prefetch categorías de productos
//           queryClient.prefetchQuery({
//             queryKey: queryKeys.productCategories,
//             staleTime: 15 * 60 * 1000,
//           }),

//           // Prefetch productos populares (si tienes esa query)
//           queryClient.prefetchQuery({
//             queryKey: [...queryKeys.products, { category: -1, popular: true }],
//             staleTime: 10 * 60 * 1000,
//           }),
//         ]);

//         console.log('✅ Catalog data prefetched successfully');
//       } catch (error) {
//         console.warn('⚠️ Error prefetching catalog data:', error);
//       }
//     };

//     const timeoutId = setTimeout(prefetchCatalogData, 3000);

//     return () => clearTimeout(timeoutId);
//   }, [queryClient]);
// };

/**
 * Hook para cargar branding inmediatamente al inicio
 * Este hook se ejecuta antes que otros para asegurar que los estilos estén disponibles
 */
export const useLoadBrandingImmediately = () => {
  const queryClient = useQueryClient()

  useEffect(() => {
    const loadBrandingImmediately = async () => {
      try {
        const brandingData = await getOrgBranding()

        if (brandingData) {
          // Guardar en cache de React Query sin hacer una segunda llamada
          queryClient.setQueryData(["orgBranding"], brandingData)

          if (brandingData.banner) {
            applyBrandingStyles(brandingData.banner)
          }
        }
      } catch (error) {
        console.warn("⚠️ Error loading branding immediately:", error)
      }
    }

    loadBrandingImmediately()
  }, [queryClient])
}

/**
 * Hook para invalidar queries relacionadas
 */
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient()

  const invalidateUserQueries = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user })
  }

  const invalidateProductQueries = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.products })
  }

  const invalidateCartQueries = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.cart })
  }

  const invalidateAllQueries = () => {
    queryClient.invalidateQueries()
  }

  return {
    invalidateUserQueries,
    invalidateProductQueries,
    invalidateCartQueries,
    invalidateAllQueries,
  }
}
